import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { listenForDeepLinks } from "@/lib/native";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import MagicLinkVerify from "@/pages/MagicLinkVerify";
import Bootstrap from "@/pages/Bootstrap";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import Jobs from "@/pages/Jobs";
import Reports from "@/pages/Reports";
import Rules from "@/pages/Rules";
import Integrations from "@/pages/Integrations";
import Alerts from "@/pages/Alerts";
import TestParser from "@/pages/TestParser";
import Monitors from "@/pages/Monitors";
import AlertChannels from "@/pages/AlertChannels";
import PortalUsers from "@/pages/PortalUsers";
import UsersAdmin from "@/pages/UsersAdmin";
import TVDashboard from "@/pages/TVDashboard";
import Mailboxes from "@/pages/Mailboxes";
import VendorIntegrations from "@/pages/VendorIntegrations";
import PublicStatus from "@/pages/PublicStatus";
import TrustCenter from "@/pages/TrustCenter";
import Incidents from "@/pages/Incidents";
import Digest from "@/pages/Digest";
import HelpDocs from "@/pages/HelpDocs";
import AccountSettings from "@/pages/AccountSettings";

// When running inside the Capacitor native shell, translate incoming deep
// links (sentryit://verify/<token> or Universal Links) into React Router
// navigations. No-op in the browser.
function NativeDeepLinkBridge() {
  useEffect(() => {
    return listenForDeepLinks((rawUrl) => {
      try {
        let path;
        if (rawUrl.startsWith("sentryit://")) {
          path = "/" + rawUrl.replace(/^sentryit:\/\/+/, "");
        } else {
          const u = new URL(rawUrl);
          path = u.pathname + u.search + u.hash;
        }
        if (path) window.history.pushState({}, "", path);
        // Fire a popstate so React Router picks up the change.
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch { /* silent */ }
    });
  }, []);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NativeDeepLinkBridge />
        <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/verify/:token" element={<MagicLinkVerify />} />
          <Route path="/auth/bootstrap" element={<Bootstrap />} />

          {/* Public status (no auth) */}
          <Route path="/status/:token" element={<PublicStatus />} />
          <Route path="/trust/:token" element={<TrustCenter />} />

          <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/app/tv" element={<ProtectedRoute><TVDashboard /></ProtectedRoute>} />
          <Route path="/app/monitors" element={<ProtectedRoute><Monitors /></ProtectedRoute>} />
          <Route path="/app/incidents" element={<ProtectedRoute><Incidents /></ProtectedRoute>} />
          <Route path="/app/digest" element={<ProtectedRoute><Digest /></ProtectedRoute>} />
          <Route path="/app/help" element={<ProtectedRoute><HelpDocs /></ProtectedRoute>} />
          <Route path="/app/vendor-integrations" element={<ProtectedRoute><VendorIntegrations /></ProtectedRoute>} />
          <Route path="/app/mailboxes" element={<ProtectedRoute><Mailboxes /></ProtectedRoute>} />
          <Route path="/app/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
          <Route path="/app/portal-users" element={<ProtectedRoute><PortalUsers /></ProtectedRoute>} />
          <Route path="/app/users" element={<ProtectedRoute><UsersAdmin /></ProtectedRoute>} />
          <Route path="/app/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
          <Route path="/app/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path="/app/rules" element={<ProtectedRoute><Rules /></ProtectedRoute>} />
          <Route path="/app/alert-channels" element={<ProtectedRoute><AlertChannels /></ProtectedRoute>} />
          <Route path="/app/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
          <Route path="/app/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
          <Route path="/app/test-parser" element={<ProtectedRoute><TestParser /></ProtectedRoute>} />
          <Route path="/app/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
